import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import {
  ArrowLeft,
  Trophy,
  Lock,
  CheckCircle,
  Star,
  Flame,
  Shield,
  Zap,
  Target,
  Award,
  Users,
  Clock
} from 'lucide-react'
import Footer from '../components/Footer'

/**
 * BadgeScreen Component
 * Display user's earned badges and achievements
 */
const BadgeScreen = () => {
  const navigate = useNavigate()
  
  // Mock user progress - in production this would come from API
  const [userBadges] = useState({
    earned: ['first-steps', 'quick-learner', 'prepared-citizen'],
    progress: {
      'week-warrior': { current: 3, required: 7 },
      'module-master': { current: 1, required: 4 },
      'simulation-expert': { current: 0, required: 10 }
    }
  })

  // All available badges
  const badges = [
    // Achievement Badges
    {
      id: 'first-steps',
      name: 'First Steps',
      description: 'Complete your first lesson',
      icon: CheckCircle,
      category: 'achievement',
      color: '#10b981'
    },
    {
      id: 'quick-learner',
      name: 'Quick Learner',
      description: 'Complete 5 lessons',
      icon: Zap,
      category: 'achievement',
      color: '#3b82f6'
    },
    {
      id: 'module-master',
      name: 'Module Master',
      description: 'Complete all 4 core modules',
      icon: Trophy,
      category: 'achievement',
      color: '#f59e0b'
    },
    {
      id: 'prepared-citizen',
      name: 'Prepared Citizen',
      description: 'Build your 72-hour kit',
      icon: Shield,
      category: 'achievement',
      color: '#8b5cf6'
    },
    
    // Streak Badges
    {
      id: 'week-warrior',
      name: 'Week Warrior',
      description: 'Maintain a 7-day streak',
      icon: Flame,
      category: 'streak',
      color: '#ef4444'
    },
    {
      id: 'month-champion',
      name: 'Month Champion',
      description: 'Maintain a 30-day streak',
      icon: Flame,
      category: 'streak',
      color: '#dc2626'
    },
    
    // Skill Badges
    {
      id: 'simulation-expert',
      name: 'Simulation Expert',
      description: 'Complete 10 simulations with 80%+ score',
      icon: Target,
      category: 'skill',
      color: '#06b6d4'
    },
    {
      id: 'quiz-master',
      name: 'Quiz Master',
      description: 'Score 100% on 5 quizzes',
      icon: Star,
      category: 'skill',
      color: '#fbbf24'
    },
    {
      id: 'family-leader',
      name: 'Family Leader',
      description: 'Create a family emergency plan',
      icon: Users,
      category: 'skill',
      color: '#ec4899'
    },
    
    // Special Badges
    {
      id: 'night-owl',
      name: 'Night Owl',
      description: 'Complete lessons after midnight',
      icon: Clock,
      category: 'special',
      color: '#6366f1'
    },
    {
      id: 'survivor-elite',
      name: 'Survivor Elite',
      description: 'Complete all content with 90%+ scores',
      icon: Award,
      category: 'special',
      color: '#f97316'
    }
  ]

  const getBadgesByCategory = (category) => {
    return badges.filter(badge => badge.category === category)
  }

  const isEarned = (badgeId) => userBadges.earned.includes(badgeId)
  
  const getProgress = (badgeId) => userBadges.progress[badgeId]

  const categories = [
    { id: 'achievement', name: 'Achievements', icon: Trophy },
    { id: 'streak', name: 'Streaks', icon: Flame },
    { id: 'skill', name: 'Skills', icon: Target },
    { id: 'special', name: 'Special', icon: Star }
  ]

  return (
    <div className="badge-screen">
      <div className="landing-ambient" />

      {/* Header */}
      <header className="badge-header">
        <div className="container">
          <motion.button
            className="back-button"
            onClick={() => navigate('/')}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
          >
            <ArrowLeft size={20} />
            <span>Back to Home</span>
          </motion.button>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <span className="eyebrow">Your Achievements</span>
            <h1 className="page-title">My Badges</h1>
            <p className="page-subtitle">
              {userBadges.earned.length} of {badges.length} badges earned
            </p>

            <div className="progress-overview">
              <motion.div
                className="progress-bar"
                initial={{ scaleX: 0 }}
                animate={{ scaleX: 1 }}
                transition={{ delay: 0.3, duration: 0.5 }}
              >
                <div
                  className="progress-fill"
                  style={{ width: `${(userBadges.earned.length / badges.length) * 100}%` }}
                />
              </motion.div>
            </div>
          </motion.div>
        </div>
      </header>

      {/* Badge Categories */}
      <main className="container badge-main">
        {categories.map((category, catIndex) => {
          const categoryBadges = getBadgesByCategory(category.id)
          const earnedInCategory = categoryBadges.filter(b => isEarned(b.id)).length

          return (
            <motion.section
              key={category.id}
              className="badge-category"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 + catIndex * 0.1 }}
            >
              <div className="category-header">
                <div className="category-title">
                  <category.icon size={24} />
                  <h2>{category.name}</h2>
                </div>
                <span className="category-count">
                  {earnedInCategory} / {categoryBadges.length}
                </span>
              </div>

              <div className="badge-grid">
                {categoryBadges.map((badge, index) => {
                  const earned = isEarned(badge.id)
                  const progress = getProgress(badge.id)
                  const Icon = badge.icon

                  return (
                    <motion.div
                      key={badge.id}
                      className={`badge-card ${earned ? 'earned' : 'locked'}`}
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: 0.3 + index * 0.05 }}
                      whileHover={{ y: -4, scale: 1.02 }}
                    >
                      <div
                        className="badge-icon"
                        style={{
                          backgroundColor: earned ? badge.color : 'rgba(255, 255, 255, 0.1)',
                          color: earned ? 'white' : 'rgba(255, 255, 255, 0.4)'
                        }}
                      >
                        {earned ? (
                          <Icon size={32} />
                        ) : (
                          <Lock size={32} />
                        )}
                      </div>

                      <h3 className="badge-name">{badge.name}</h3>
                      <p className="badge-description">{badge.description}</p>

                      {!earned && progress && (
                        <div className="badge-progress">
                          <div className="progress-text">
                            {progress.current} / {progress.required}
                          </div>
                          <div className="progress-bar-small">
                            <div
                              className="progress-fill-small"
                              style={{ width: `${(progress.current / progress.required) * 100}%` }}
                            />
                          </div>
                        </div>
                      )}

                      {earned && (
                        <div className="earned-label">
                          <CheckCircle size={16} />
                          <span>Earned!</span>
                        </div>
                      )}
                    </motion.div>
                  )
                })}
              </div>
            </motion.section>
          )
        })}
      </main>

      <Footer />

      <style jsx>{`
        .badge-screen {
          position: relative;
          min-height: 100vh;
          background: radial-gradient(circle at 15% 20%, rgba(249, 115, 22, 0.08), transparent 32%),
                      radial-gradient(circle at 80% 0%, rgba(59, 130, 246, 0.1), transparent 35%),
                      linear-gradient(135deg, #0b1220 0%, #0f172a 50%, #0b1326 100%);
          color: var(--neutral-50);
        }

        .badge-screen .landing-ambient {
          position: absolute;
          inset: 0;
          background-image:
            linear-gradient(120deg, rgba(255, 255, 255, 0.04) 1px, transparent 1px),
            linear-gradient(0deg, rgba(255, 255, 255, 0.04) 1px, transparent 1px);
          background-size: 120px 120px;
          opacity: 0.25;
          pointer-events: none;
        }

        .badge-header {
          position: relative;
          z-index: 1;
          background: linear-gradient(135deg, rgba(249, 115, 22, 0.12), rgba(220, 38, 38, 0.08));
          border-bottom: 1px solid rgba(255, 255, 255, 0.08);
          padding: var(--spacing-xl) 0;
          text-align: center;
        }

        .badge-main {
          position: relative;
          z-index: 1;
          padding: var(--spacing-xl) 0;
        }

        .back-button {
          display: inline-flex;
          align-items: center;
          gap: var(--spacing-sm);
          background: rgba(255, 255, 255, 0.06);
          border: 1px solid rgba(255, 255, 255, 0.12);
          color: var(--neutral-50);
          cursor: pointer;
          padding: var(--spacing-sm) var(--spacing-md);
          margin-bottom: var(--spacing-lg);
          border-radius: var(--radius-md);
          transition: all 0.2s;
        }

        .back-button:hover {
          background: rgba(255, 255, 255, 0.1);
          border-color: rgba(255, 255, 255, 0.2);
        }

        .eyebrow {
          display: inline-flex;
          padding: 0.35rem 0.75rem;
          border-radius: 999px;
          border: 1px solid rgba(255, 255, 255, 0.1);
          color: rgba(255, 255, 255, 0.82);
          background: rgba(255, 255, 255, 0.05);
          font-size: 0.85rem;
          letter-spacing: -0.01em;
          margin-bottom: 0.75rem;
        }

        .page-title {
          font-size: clamp(1.75rem, 4vw, 2.25rem);
          font-weight: 800;
          letter-spacing: -0.02em;
          margin-bottom: var(--spacing-sm);
          color: var(--neutral-50);
        }

        .page-subtitle {
          color: rgba(255, 255, 255, 0.7);
          margin-bottom: var(--spacing-lg);
        }

        .progress-overview {
          max-width: 400px;
          margin: 0 auto;
        }

        .progress-bar {
          height: 10px;
          background: rgba(255, 255, 255, 0.1);
          border-radius: var(--radius-full);
          overflow: hidden;
          transform-origin: left;
        }

        .progress-fill {
          height: 100%;
          background: linear-gradient(90deg, #f97316, #dc2626);
          transition: width 0.5s ease;
        }

        /* Badge Categories */
        .badge-category {
          margin-bottom: var(--spacing-xl);
        }

        .category-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: var(--spacing-lg);
        }

        .category-title {
          display: flex;
          align-items: center;
          gap: var(--spacing-sm);
          color: var(--neutral-50);
        }

        .category-title svg {
          color: #f97316;
        }

        .category-title h2 {
          font-size: 1.25rem;
          margin: 0;
          font-weight: 700;
        }

        .category-count {
          color: rgba(255, 255, 255, 0.7);
          font-weight: bold;
          background: rgba(255, 255, 255, 0.06);
          padding: 0.25rem 0.75rem;
          border-radius: 999px;
          font-size: 0.875rem;
        }

        .badge-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
          gap: var(--spacing-md);
        }

        .badge-card {
          background: rgba(255, 255, 255, 0.04);
          border: 1px solid rgba(255, 255, 255, 0.08);
          border-radius: 1rem;
          padding: var(--spacing-lg);
          text-align: center;
          transition: all 0.2s;
          position: relative;
        }

        .badge-card.earned {
          border-color: rgba(249, 115, 22, 0.4);
          background: rgba(249, 115, 22, 0.08);
        }

        .badge-icon {
          width: 64px;
          height: 64px;
          border-radius: var(--radius-full);
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto var(--spacing-md);
          transition: all 0.3s;
        }

        .badge-card.earned .badge-icon {
          animation: glow 2s infinite;
        }

        @keyframes glow {
          0%, 100% {
            box-shadow: 0 0 0 0 rgba(249, 115, 22, 0.4);
          }
          50% {
            box-shadow: 0 0 20px 4px rgba(249, 115, 22, 0.2);
          }
        }

        .badge-name {
          font-size: 1rem;
          font-weight: 700;
          margin-bottom: var(--spacing-xs);
          color: var(--neutral-50);
        }

        .badge-description {
          font-size: 0.875rem;
          color: rgba(255, 255, 255, 0.6);
          margin-bottom: var(--spacing-md);
        }

        .badge-card.locked .badge-name,
        .badge-card.locked .badge-description {
          opacity: 0.6;
        }

        .badge-progress {
          margin-top: var(--spacing-sm);
        }

        .progress-text {
          font-size: 0.75rem;
          font-weight: bold;
          color: #f97316;
          margin-bottom: var(--spacing-xs);
        }

        .progress-bar-small {
          height: 4px;
          background: rgba(255, 255, 255, 0.1);
          border-radius: var(--radius-full);
          overflow: hidden;
        }

        .progress-fill-small {
          height: 100%;
          background: linear-gradient(90deg, #f97316, #dc2626);
          transition: width 0.3s ease;
        }

        .earned-label {
          position: absolute;
          top: var(--spacing-sm);
          right: var(--spacing-sm);
          display: flex;
          align-items: center;
          gap: var(--spacing-xs);
          color: #22c55e;
          font-size: 0.75rem;
          font-weight: bold;
        }

        @media (max-width: 768px) {
          .badge-grid {
            grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
          }

          .badge-card {
            padding: var(--spacing-md);
          }

          .badge-icon {
            width: 48px;
            height: 48px;
          }

          .badge-icon svg {
            width: 24px;
            height: 24px;
          }
        }
      `}</style>
    </div>
  )
}

export default BadgeScreen